class MutFeaturedCollection extends HTMLElement {
  constructor() {
    super();
    this.prevBtn = this.querySelector(".prevBtn");
    this.nextBtn = this.querySelector(".nextBtn");
    this.content = this.querySelector(".grid-overflow-wrapper");
    this.scrollControl = this.querySelector("footer.scroll-control");
    this.thumb = this.scrollControl.querySelector(".thumb");
    this.scrollbar = this.scrollControl.querySelector(".scrollbar");
    
    // Cache frequently used values
    this.isMobile = this.checkMobileDevice();
    this.scrollAmount = 0; // Will be calculated on first use
    
    // Throttle scroll event for better performance
    this.scrollThrottleTimer = null;
    this.scrollThrottleDelay = 16; // ~60fps
    
    // Initialize
    this.updateThumbPosition();
    
    // Bind methods to preserve 'this' context
    this.handleScroll = this.handleScroll.bind(this);
    this.scrollBy = this.scrollBy.bind(this);
    
    // Use passive listeners for better performance
    this.content.addEventListener("scroll", this.handleScroll, { passive: true });
    this.prevBtn.addEventListener("click", this.scrollBy);
    this.nextBtn.addEventListener("click", this.scrollBy);
    
    // Update on resize to handle responsive changes
    this.resizeObserver = new ResizeObserver(this.updateThumbPosition.bind(this));
    this.resizeObserver.observe(this.content);
  }
  
  disconnectedCallback() {
    // Clean up to prevent memory leaks
    this.content.removeEventListener("scroll", this.handleScroll);
    this.prevBtn.removeEventListener("click", this.scrollBy);
    this.nextBtn.removeEventListener("click", this.scrollBy);
    this.resizeObserver.disconnect();
  }

  handleScroll() {
    // Throttle scroll events for performance
    if (!this.scrollThrottleTimer) {
      this.scrollThrottleTimer = setTimeout(() => {
        this.updateThumbPosition();
        this.scrollThrottleTimer = null;
      }, this.scrollThrottleDelay);
    }
  }

  updateThumbPosition() {
    const contentWidth = this.content.scrollWidth;
    const contentScrollLeft = this.content.scrollLeft;
    const containerWidth = this.content.clientWidth;
    
    // Check if scrolling is needed
    if (this.isScrollable(contentWidth, containerWidth)) return;

    // Calculate scroll parameters once
    const scrollbarWidth = this.scrollbar.clientWidth;
    const scrollRatio = containerWidth / contentWidth;
    const thumbWidth = Math.floor(Math.max(scrollRatio * scrollbarWidth, 20));
    const maxScrollRight = contentWidth - containerWidth;
    
    // Calculate thumb position only if there's scrollable content
    if (maxScrollRight <= 0) return;
    
    const maxThumbRight = scrollbarWidth - thumbWidth;
    const thumbPosition = (contentScrollLeft / maxScrollRight) * maxThumbRight;
    
    // Update UI
    this.updateButtonState(thumbPosition, maxThumbRight);
    
    // Use transform instead of left for better performance
    this.thumb.style.width = `${thumbWidth}px`;
    this.thumb.style.transform = `translateX(${thumbPosition}px)`;
    
    // Calculate scroll amount once if not set
    if (!this.scrollAmount) {
      this.scrollAmount = contentWidth * 0.2;
    }
  }

  isScrollable(content, container) {
    const remainWidth = content - container;
    // Avoid reflow by using classList.toggle with a condition
    const shouldHide = remainWidth < 20 && !this.isMobile;
    this.scrollControl.classList.toggle("scroll-control__hiden", shouldHide);
    return shouldHide;
  }

  scrollBy(event) {
    // Calculate scroll amount only once, if needed
    if (!this.scrollAmount) {
      this.scrollAmount = this.content.scrollWidth * 0.2;
    }
    
    const direction = event.currentTarget.className === "prevBtn" ? -1 : 1;
    
    // Use scrollTo with behavior: smooth for better performance
    this.content.scrollTo({
      left: this.content.scrollLeft + (this.scrollAmount * direction),
      behavior: 'smooth'
    });
  }

  checkMobileDevice() {
    // Only check once during initialization
    return /Mobi|Android|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(
      navigator.userAgent
    );
  }

  updateButtonState(thumbPosition, maxThumbRight) {
    // Batch DOM operations
    const prevDisabled = thumbPosition <= 0;
    const nextDisabled = thumbPosition >= maxThumbRight;
    
    if (this.prevBtn.disabled !== prevDisabled) {
      this.prevBtn.disabled = prevDisabled;
    }
    
    if (this.nextBtn.disabled !== nextDisabled) {
      this.nextBtn.disabled = nextDisabled;
    }
  }
}

// Use requestAnimationFrame to defer component initialization
if (typeof customElements !== 'undefined') {
  customElements.define("mut-featured-collection", MutFeaturedCollection);
}